/**
 * Composed-deck primitives.
 *
 * Pure functions for assembling a `SyncCardProps` (or `SyncDeckMeta`)
 * from its split-canonical halves: the shared part lives in
 * `useStudyGroupStore`; the personal half lives in
 * `useFlashkaStore.cards` / `useFlashkaStore.deckProperties` — the
 * canonical local rows where personal-scope writes land. For a
 * personal-only deck the compositor is bypassed entirely.
 *
 * The previous third-overlay parameter has been removed —
 * `linkedCardOverlay` / `linkedDeckOverlay` entities were inert
 * placeholders that no writer ever populated. Reads now go straight
 * from the local cards / deckProperties slices.
 *
 * No React, no Zustand, no DO primitives. Side-effect-free and
 * trivially unit-testable.
 */

import {
  IDENTITY_DECK_FIELDS,
  PERSONAL_CARD_FIELDS,
  PERSONAL_DECK_FIELDS,
} from '@flashka/shared/sync-engine/schema/field-scopes';
import type {
  CardAction,
  CardStudyState,
  CardTag,
  SyncCardProps,
  SyncChatMessage,
  SyncDeckMeta,
} from '@flashka/shared/sync-engine/schema/shared-types';

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/**
 * Scope defaults for the personal half of a card. Returned by the
 * compositor when no local personal row exists for a shared card. The
 * values mirror what `cards.add()` stamps on a brand-new card.
 *
 * Returns fresh array/object literals each call, so no two composed cards
 * share a defaults array. That is NOT a licence to mutate one: composed cards
 * are identity-cached and handed out again on later calls
 * (`composeCardCached` / `composeCardsMap`), so callers must treat them — and
 * these arrays inside them — as read-only, like any store row.
 */
export function scopeDefaultsForCard(): {
  notes: string;
  actions: CardAction[];
  studyState: CardStudyState[];
  chat: SyncChatMessage[];
  tags: CardTag[];
  starred: false;
  personalUpdatedAtISO: undefined;
} {
  return {
    notes: '',
    actions: [],
    studyState: [],
    chat: [],
    tags: [],
    starred: false,
    personalUpdatedAtISO: undefined,
  };
}

/**
 * Scope defaults for the personal half of a deck-meta. Returned when
 * no local `deckProperties[deckId]` row carries the personal-scope
 * fields yet. The deck still has a canonical shared half (sg-side) —
 * these are just the fields a member has yet to customise.
 */
export function scopeDefaultsForDeck(): {
  personalFolderId: undefined;
  personalDeckSortKey: undefined;
  personalUpdatedAtISO: undefined;
} {
  return {
    personalFolderId: undefined,
    personalDeckSortKey: undefined,
    personalUpdatedAtISO: undefined,
  };
}

// ---------------------------------------------------------------------------
// Card compositor
// ---------------------------------------------------------------------------

/**
 * Compose a card from its shared half + the local personal-scope row.
 *
 * Returns `undefined` when `shared` is missing — the SG hasn't loaded
 * this card yet (loading state) OR the card was deleted on the SG side.
 * The composed `id` always matches `shared.id`.
 *
 * Order: defaults < shared < `personal`. `personal` carries the local
 * personal-scope fields (starred, studyState, actions, notes, tags,
 * chat). The shared half NEVER carries personal-scope keys (lazy strip
 * enforces this on the SG side).
 *
 * No mutation of inputs. Result is a fresh object.
 */
export function composeCard(
  shared: SyncCardProps | undefined,
  personal: SyncCardProps | undefined,
): SyncCardProps | undefined {
  if (!shared) return undefined;
  const defaults = scopeDefaultsForCard();
  const composed = {
    ...defaults,
    ...shared,
  } as SyncCardProps;
  if (personal && personal.id === shared.id) {
    for (const key of PERSONAL_CARD_FIELDS) {
      const v = (personal as Record<string, unknown>)[key as string];
      if (v !== undefined) {
        (composed as Record<string, unknown>)[key as string] = v;
      }
    }
  }
  return composed;
}

/**
 * Compose every card in a shared-half map with its matching personal row.
 *
 * Inputs:
 *   - `sharedMap`: `useStudyGroupStore.groups[gid].cards[deckId]`
 *   - `personalMap` (optional): `useFlashkaStore.cards[deckId]`, the local
 *     storage where personal-scope fields live.
 *
 * Output: `Record<cardId, SyncCardProps>` containing every card from
 * `sharedMap`, composed with its matching personal row if present.
 *
 * Cards present in `personalMap` but missing from `sharedMap` are
 * dropped — without a shared half we have no card to render. (This
 * happens when a member deletes a card from the group; the local
 * personal row lingers until the SG broadcasts the tombstone, then the
 * next compose drops it.)
 *
 * Identity-stable: the same two input maps return the same output map, and
 * a card whose shared half and personal row are both unchanged comes back
 * as the same composed object (see the caches below).
 */
export function composeCardsMap(
  sharedMap: Record<string, SyncCardProps> | undefined,
  personalMap?: Record<string, SyncCardProps>,
): Record<string, SyncCardProps> {
  if (!sharedMap) return {};
  // Same two input maps → the same composed map (both are immutable store
  // slices), so a re-render or an unrelated store tick costs one lookup.
  const byPersonal = composedMapCache.get(sharedMap);
  const personalKey: object = personalMap ?? NO_PERSONAL;
  const hit = byPersonal?.get(personalKey);
  if (hit) return hit;
  const composed: Record<string, SyncCardProps> = {};
  for (const cardId in sharedMap) {
    if (!Object.prototype.hasOwnProperty.call(sharedMap, cardId)) continue;
    const out = composeCardCached(sharedMap[cardId], personalMap?.[cardId]);
    if (out) composed[cardId] = out;
  }
  if (byPersonal) byPersonal.set(personalKey, composed);
  else composedMapCache.set(sharedMap, new WeakMap([[personalKey, composed]]));
  return composed;
}

/**
 * Identity caches for the compositor. Both halves are immutable once their
 * store publishes them (every write builds a new card / map object), so a
 * composed value is a pure function of its two input OBJECTS:
 *
 * - map level: `sharedMap → personalMap | NO_PERSONAL → composed map`;
 * - card level: one slot per shared card holding the personal row it was last
 *   composed with. One personal edit on a 5k linked deck then recomposes ONE
 *   card and hands every other row back by identity, so memoised rows and the
 *   per-card fact caches downstream stay warm.
 *
 * WeakMaps throughout: a superseded map or card drops out with its input.
 * Composed cards are shared across calls — treat them as read-only, exactly
 * like the store rows a personal-only deck hands out directly.
 */
const NO_PERSONAL: object = Object.freeze({});
const composedMapCache = new WeakMap<
  object,
  WeakMap<object, Record<string, SyncCardProps>>
>();
const composedCardCache = new WeakMap<
  object,
  { personal: SyncCardProps | undefined; composed: SyncCardProps | undefined }
>();

/**
 * {@link composeCard} with the per-shared-card identity slot: the same
 * `(shared, personal)` objects return the same composed card.
 */
export function composeCardCached(
  shared: SyncCardProps | undefined,
  personal: SyncCardProps | undefined,
): SyncCardProps | undefined {
  if (!shared) return undefined;
  const slot = composedCardCache.get(shared);
  if (slot && slot.personal === personal) return slot.composed;
  const composed = composeCard(shared, personal);
  composedCardCache.set(shared, { personal, composed });
  return composed;
}

// ---------------------------------------------------------------------------
// Deck-meta compositor
// ---------------------------------------------------------------------------

/**
 * Compose a deck's meta from its shared half + the local personal row.
 *
 * The shared half lives canonically in `SGDeckProps` (structurally a
 * subset of `SyncDeckMeta`); the personal row supplies `personalFolderId`
 * / `personalDeckSortKey` / `personalUpdatedAtISO` and `linkedGroups`.
 *
 * Returns `undefined` when `shared` is absent. For the deck-meta
 * compositor, "shared absent" means the SG sync-init hasn't landed
 * yet (or the deck was unshared and the SG broadcast hasn't dropped
 * the row yet).
 */
export function composeDeckMeta(
  shared: Partial<SyncDeckMeta> | undefined,
  personal: SyncDeckMeta | undefined,
): SyncDeckMeta | undefined {
  if (!shared) return undefined;
  const defaults = scopeDefaultsForDeck();
  const composed = {
    ...defaults,
    ...shared,
  } as SyncDeckMeta;
  if (personal) {
    for (const key of PERSONAL_DECK_FIELDS) {
      const v = (personal as Record<string, unknown>)[key as string];
      if (v !== undefined) {
        (composed as Record<string, unknown>)[key as string] = v;
      }
    }
    // Identity is the LOCAL deck's: the group row's `id` is the group's key
    // for the deck, not this user's deck id, and it carries no `deckPaths` /
    // `linkedGroups` at all. A writer that read the composed row and wrote
    // back under its `id` used to mint a phantom personal deck keyed by the
    // group's id.
    for (const key of IDENTITY_DECK_FIELDS) {
      const v = (personal as Record<string, unknown>)[key as string];
      if (v !== undefined) {
        (composed as Record<string, unknown>)[key as string] = v;
      }
    }
  }
  return composed;
}
